import { useEffect, useRef } from "react";
import { ApiError } from "@/api/client";
import { getUserFacingErrorMessage } from "@/api/userFacingError";
import {
  ReplacementComposer,
  type CancellationComposerSubmission,
  type ReplacementComposerSubmission,
} from "@/components/orders/ReplacementComposer";
import type {
  DecideReturnPayload,
  EnrichedCheckinItem,
} from "@/features/checkin/types";
import { selectLatestActiveReturnLot } from "@/features/checkin/itemDisplay";

export function requireOpenedReturnId(returnId: string | null) {
  if (!returnId) {
    throw new ApiError(409, "stale return decision", "stale_return_decision");
  }
  return returnId;
}

export function ReturnResolutionModal({
  appearance,
  open,
  item,
  allowReplacement,
  allowCancellation,
  available,
  initialDecision = "SUBSTITUIDO",
  pending,
  onClose,
  onResolve,
  onSuccess,
}: {
  appearance?: "notification";
  open: boolean;
  item: EnrichedCheckinItem;
  allowReplacement: boolean;
  allowCancellation: boolean;
  available: boolean;
  initialDecision?: "SUBSTITUIDO" | "DEVOLVIDO";
  pending: boolean;
  onClose: () => void;
  onResolve: (payload: DecideReturnPayload) => Promise<void>;
  onSuccess?: (result: string) => void;
}) {
  const activeReturnLot = selectLatestActiveReturnLot(item.return_lots);
  const openedReturnId = useRef<string | null>(null);
  const receivedQuantity = item.received_total ?? item.received_quantity;
  const requestedQuantity =
    item.original_expected_quantity ?? item.expected_quantity;
  const pendingQuantity =
    item.pending_quantity ?? Math.max(requestedQuantity - receivedQuantity, 0);

  useEffect(() => {
    if (!open) {
      openedReturnId.current = null;
      return;
    }
    if (!available || !activeReturnLot) {
      onClose();
      return;
    }
    if (
      openedReturnId.current &&
      openedReturnId.current !== activeReturnLot.id
    ) {
      onClose();
      return;
    }
    openedReturnId.current = activeReturnLot.id;
  }, [activeReturnLot, available, onClose, open]);

  async function replace(submission: ReplacementComposerSubmission) {
    await onResolve({
      decision: "SUBSTITUIDO",
      expected_return_id: requireOpenedReturnId(openedReturnId.current),
      ...submission,
    });
  }

  async function cancel(submission: CancellationComposerSubmission) {
    await onResolve({
      decision: "DEVOLVIDO",
      expected_return_id: requireOpenedReturnId(openedReturnId.current),
      ...submission,
    });
  }

  return (
    <ReplacementComposer
      appearance={appearance}
      open={open}
      original={{
        productId: item.product_id,
        productName: item.product_name,
        supplierId: item.supplier_id,
        supplierName: item.supplier_name,
        unitType: item.unit_type,
        requestedQuantity,
        receivedQuantity,
        pendingQuantity,
        realPrice: item.real_price,
        finalPrice: item.final_price,
      }}
      allowReplacement={allowReplacement}
      allowCancellation={allowCancellation}
      initialMode={
        initialDecision === "DEVOLVIDO" ? "cancellation" : "replacement"
      }
      pending={pending}
      onClose={onClose}
      onReplace={replace}
      onCancelItem={cancel}
      onSuccess={(_mode, result) => onSuccess?.(result)}
      getErrorMessage={(error) =>
        getUserFacingErrorMessage(
          error,
          "Não foi possível resolver o item. Tente novamente.",
        )
      }
    />
  );
}
